import { useAbility } from '@casl/react'
import { Box, IconButton, Link, TableRow, Tooltip } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { TrashIcon } from 'assets/icons/trash'
import { AxiosResponse } from 'axios'
import { LabelBox } from 'components/label-box'
import { AbilityContext } from 'config/ability'
import { axiosPatch } from 'connectors/axiosPatch'
import { useAlert } from 'context/AlertContext'
import { useAppContext } from 'context/AppContext'
import { useAuthContext } from 'context/AuthContext'
import { APIApplication } from 'types/Application'
import { renderCells } from 'utils/renderCells'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import { applicationStatuses, StatusTypes } from '../applicationStatuses'
import { StatusChip } from '../Details/features/status-chip'
import { useNavigate } from 'react-router-dom'
import { EyeIcon } from 'assets/icons/eye-icon'
import { PenIcon } from 'assets/icons/pen'
import { KeyIcon } from 'assets/icons/key-icon'
import { EditCell } from 'components/ActionDropdown/ActionDropdown'
import PageLoader from 'components/PageLoader'

type ApplicationRowProps = APIApplication & {
  filtersParams: string
  setElementToDelete: (val: APIApplication) => void
  setElementToHardDelete: (val: APIApplication) => void
}

export const ApplicationRow = ({
  filtersParams,
  setElementToDelete,
  setElementToHardDelete,
  ...row
}: ApplicationRowProps) => {
  const queryClient = useQueryClient()
  const ability = useAbility(AbilityContext)
  const { t } = useTranslation()
  const { token, refreshToken, get2fa } = useAuthContext()
  const { handleRefetch } = useAppContext()
  const { changeMessage } = useAlert()
  const navigate = useNavigate()

  const {
    mutate: anonymize,
    reset: resetAnonymize,
    isLoading,
  } = useMutation(
    async ({ id, tfa }: { id: string; tfa: string }) =>
      await axiosPatch<AxiosResponse<string>>(token, refreshToken, `applications/anonymize/${id}`, { tfa }),
    {
      onSuccess: () => {
        void queryClient.refetchQueries(['markets-query'])
        handleRefetch()
      },
      onSettled: data => {
        if (data !== undefined && typeof data === 'string' && data === '') {
          changeMessage(t('common.success'), 'success', resetAnonymize)
        }
        if (data !== undefined && 'error' in data && data.error !== undefined) {
          changeMessage(data?.error?.data?.message, 'error', resetAnonymize)
        }
      },
    }
  )

  return (
    <TableRow>
      {renderCells([
        {
          label: 'ID',
          value: <LabelBox first={`#${row.application_id}`} />,
        },
        {
          label: t('common.customer'),
          value: (
            <LabelBox
              first={
                <Link href={`/applications/${row.application_id}?${filtersParams.toString()}`}>
                  {row.application_fullName}
                </Link>
              }
              second={row.application_email}
            />
          ),
        },
        {
          label: t('common.marketName'),
          value: <LabelBox first={row.market_name ?? 'N/A'} second={row.market_description || ''} />,
        },
        {
          label: t('common.createdAt'),
          value: (
            <LabelBox
              first={format(new Date(row.application_createdAt), 'MMM dd, yyyy')}
              second={format(new Date(row.application_createdAt), 'HH:mm:ss')}
            />
          ),
        },
        {
          label: `${t('common.createdAt')} local`,
          value: (
            <LabelBox
              first={
                row.application_createdAtLocal ? format(new Date(row.application_createdAtLocal), 'MMM dd, yyyy') : '-'
              }
              second={
                row.application_createdAtLocal ? format(new Date(row.application_createdAtLocal), 'HH:mm:ss') : '-'
              }
            />
          ),
        },
        {
          label: t('common.status'),
          value: row.application_status in applicationStatuses ? <StatusChip status={row.application_status} /> : null,
        },
      ])}
      <EditCell>
        <Box display="flex" gap={0.5}>
          {ability.can('read', 'Applications') && (
            <Tooltip title={t('common.details')}>
              <IconButton
                onClick={(): void => {
                  navigate(`/applications/${row.application_id}`, { state: { ...row } })
                }}
              >
                <EyeIcon size={18} />
              </IconButton>
            </Tooltip>
          )}
          {ability.can('update', 'Applications') && row.application_status !== StatusTypes.Deleted && (
            <Tooltip title={t('common.edit')}>
              <IconButton
                onClick={(): void => {
                  navigate(`/applications/edit/${row.application_id}`)
                }}
              >
                <PenIcon size={18} />
              </IconButton>
            </Tooltip>
          )}
          {ability.can('hardDelete', 'Applications') && (
            <Tooltip title={t('common.anonymize')}>
              <IconButton
                onClick={(): void => {
                  void get2fa(async tfa => {
                    anonymize({ id: row.application_id.toString(), tfa })
                  })
                }}
              >
                <KeyIcon size={18} />
              </IconButton>
            </Tooltip>
          )}
          {ability.can('delete', 'Applications') && row.application_status !== StatusTypes.Deleted && (
            <Tooltip title={t('common.delete')}>
              <IconButton
                onClick={(): void => {
                  setElementToDelete(row)
                }}
              >
                <TrashIcon size={18} />
              </IconButton>
            </Tooltip>
          )}
          {ability.can('hardDelete', 'Applications') && row.application_status === StatusTypes.Deleted && (
            <Tooltip title={`"HARD" ${t('common.delete')}`}>
              <IconButton
                onClick={(): void => {
                  setElementToHardDelete(row)
                }}
                color="error"
              >
                <TrashIcon size={18} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </EditCell>
      {isLoading && <PageLoader />}
    </TableRow>
  )
}
